/** @jsxImportSource @emotion/react */
import * as ui from "@material-ui/core";
import { definitions } from "../../../schema/api";
import { createShareResource } from "../../../const/definitions/share_resource";

import Delete from "@material-ui/icons/Delete";
import { Folder, InsertDriveFile } from "@material-ui/icons";

const labels: { [key: string]: string } = {
  addFile: "資料を追加",
  addFolder: "フォルダを追加",
  delete: "削除",
  fileName: "資料名",
  folderName: "フォルダ名",
  comment: "コメント",
};

const styles = {
  wrapper: {
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "space-between",
    border: "solid 1px #cccccc",
    borderRadius: "6px",
    width: "100%",
    margin: "0 8px",
    padding: "1rem",
  },
  icon: {
    display: "flex",
    justifyContent: "center",
    ["& > svg"]: {
      width: "100px",
      height: "100px",
    },
  },
  inputArea: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
  },
};

export type UploadingResource = (definitions["ShareResource"] & { file?: any });

interface InputProps {
  onFileChanged: (resources: UploadingResource[]) => void;
  resources: definitions["ShareResource"][];
  parentId?: number;
}

function Input(props: InputProps) {
  const onNameChange = (name: string, index: number) => {
    props.resources[index].name = name;
    props.onFileChanged(props.resources);
  };

  const onCommentChange = (comment: string, index: number) => {
    props.resources[index].comment = comment;
    props.onFileChanged(props.resources);
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const resource: UploadingResource = createShareResource();
    const eventTarget = event.target;
    if (eventTarget) {
      const file = eventTarget.files?.[0];
      resource.name = file?.name || '';
      resource.type = file?.type || '';
      resource.parent_id = props.parentId;
      resource.file = file;
      props.resources.push(resource);
      eventTarget.value = '';
      if (file && file.type.match(/^image/)) {
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e: any) => {
            const data = e.target.result;
            const base64 = data.split(",")[1];
            resolve(base64);
          };
          reader.onerror = (error) => {
            console.log(error);
            reject(error);
          };
          reader.readAsDataURL(file);
        }).then((data: any) => {
          resource.content = data;
          props.onFileChanged(props.resources);
        });
      } else {
        props.onFileChanged(props.resources);
      }
    }
  };

  const onFileDelete = (index: number) => {
    if (props.resources[index]) {
      props.resources.splice(index, 1);
    }
    props.onFileChanged(props.resources);
  };

  const onFolderAdd = () => {
    const resource = createShareResource();
    resource.type = "folder";
    resource.parent_id = props.parentId;
    props.resources.push(resource);
    props.onFileChanged(props.resources);
  };

  return (
    <ui.Grid container spacing={4}>
      {props.resources.map((resource, i) => (
        <ui.Grid
          key={`share-resource-preview-grid-${i}`}
          item
          container
          spacing={2}
          xs={12}
          sm={6}
          justifyContent="center"
          direction="row"
        >
          <div css={styles.wrapper}>
            <ui.Grid item>
              {
                resource.type === "folder" ? (
                  <div css={styles.icon}>
                    <Folder />
                  </div>
                ) : resource.type.match(/^image/) ? (
                  <ui.ImageList rowHeight={160} cols={1}>
                    <ui.ImageListItem key={`share-resource-preview-${i}`} cols={1}>
                      <img
                        src={resource.path ? `/${resource.path}`: `data:${resource.type};base64,${resource.content}`}
                        id={`thumb-${i}`}
                        alt="share-resource-thumbnail"
                      />
                    </ui.ImageListItem>
                  </ui.ImageList>
                ) : (
                  <div css={styles.icon}>
                    <InsertDriveFile />
                  </div>
                )
              }
            </ui.Grid>
            <div css={styles.inputArea}>
              <ui.TextField
                id={`share-resource-name-${i}`}
                fullWidth
                label={resource.type === "folder" ? labels.folderName : labels.fileName}
                value={resource.name}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => onNameChange(e.target.value, i)}
              />
              <ui.IconButton
                component="label"
                onClick={() => onFileDelete(i)}
              >
                <Delete />
              </ui.IconButton>
            </div>
          </div>
        </ui.Grid>
      ))}

      <ui.Grid container item xs={12} spacing={2} justifyContent="flex-start">
        <ui.Grid item>
          <ui.Button variant="contained" component="label">
            {labels.addFile}
            <input
              type="file"
              hidden
              onChange={onFileChange}
            />
          </ui.Button>
        </ui.Grid>
        <ui.Grid item>
          <ui.Button variant="contained" component="label" onClick={onFolderAdd}>
            {labels.addFolder}
          </ui.Button>
        </ui.Grid>
      </ui.Grid>
    </ui.Grid>
  );
}

export { Input };
export type { InputProps };
